import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { App } from './App'
import { AuthGate } from './auth/AuthGate'
import { StrataProvider } from './data/StrataProvider'
import './styles.css'

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  void prepareOfflineMode()
}

async function prepareOfflineMode() {
  try {
    await navigator.serviceWorker.register(new URL('/sw.js', window.location.origin))
    await navigator.serviceWorker.ready
    if (!navigator.serviceWorker.controller) {
      await new Promise<void>((resolve) => {
        const timeout = window.setTimeout(resolve, 3000)
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          window.clearTimeout(timeout)
          resolve()
        }, { once: true })
      })
    }
    const shell = [
      ...Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]'), (node) => sameOriginPath(node.src)),
      ...Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'), (node) => sameOriginPath(node.href)),
    ]
    await cacheDocumentShell()
    await Promise.allSettled(shell.map((url) => fetch(url, { credentials: 'same-origin', cache: 'reload' })))
    void cacheExerciseImages()
  } catch (error) {
    console.warn('El modo offline no pudo prepararse.', error)
  }
}

async function cacheExerciseImages() {
  try {
    const catalogResponse = await fetch(sameOriginPath('/exercise-catalog.json'), { credentials: 'same-origin', cache: 'reload' })
    if (!catalogResponse.ok) return
    const paths = await catalogResponse.json() as unknown
    if (!Array.isArray(paths)) return
    let cursor = 0
    const workers = Array.from({ length: 6 }, async () => {
      while (cursor < paths.length) {
        const path = paths[cursor++]
        if (typeof path === 'string' && path.startsWith('/exercises/')) {
          await fetch(sameOriginPath(path), { credentials: 'same-origin', cache: 'reload' }).catch(() => undefined)
        }
      }
    })
    await Promise.all(workers)
  } catch {
    // The catalogue remains available online even if the device declines offline storage.
  }
}

function sameOriginPath(value: string) {
  const url = new URL(value, window.location.origin)
  url.username = ''
  url.password = ''
  return url.href
}

async function cacheDocumentShell() {
  const controller = navigator.serviceWorker.controller
  if (!controller) return
  const page = document.documentElement.cloneNode(true) as HTMLElement
  page.querySelector('#root')?.replaceChildren()
  const html = `<!doctype html>\n${page.outerHTML}`
  await new Promise<void>((resolve) => {
    const channel = new MessageChannel()
    const timeout = window.setTimeout(resolve, 2000)
    channel.port1.onmessage = () => {
      window.clearTimeout(timeout)
      resolve()
    }
    controller.postMessage({ type: 'CACHE_DOCUMENT', html }, [channel.port2])
  })
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AuthGate>{(user) => <StrataProvider accountId={user.email}><App /></StrataProvider>}</AuthGate>
  </StrictMode>,
)
